import { useCallback, useMemo, useState } from "react";

import NoteShareSheet from "@/features/notes/components/organisms/NoteShareSheet";
import NotesItemView from "./NotesItemView";
import { handleCreateOrDeleteNote } from "@/features/notes/utils/handleDataNote";

import { useCurrentUser } from "@/features/auth";
import { AnalyticsTracker } from "@/features/analytics";
import {
  NotesNoteFragment,
  useNotesDeleteNoteMutation,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import useRecordUserGoal, { GoalType } from "@/hooks/useRecordUserGoal";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";

/** Props for NotesItemContainer */
interface Props {
  note: NotesNoteFragment;
}

/**
 * Container component for note item with delete and share functionality.
 * Compares note.userId against the current user's ID to determine ownership,
 * which controls whether delete and share actions are available.
 * Hides the Share button when the org has defaultNoteVisibility enabled,
 * since all members already have visibility into all notes.
 * @param root0 - Component props
 * @param root0.note - Note data object to display and manage
 */
const NotesItemContainer = ({ note }: Props) => {
  const currentUser = useCurrentUser();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const isOwner = useMemo(
    () => !!currentUser?.id && currentUser.id === note.userId,
    [currentUser?.id, note.userId]
  );

  const toast = useToastNotification();
  const recordUserGoal = useRecordUserGoal({
    goalType: GoalType.DeleteNote,
    value: "",
  });
  const [deleteNote, { loading }] = useNotesDeleteNoteMutation();
  const isSmallScreen = useMobileScreen();
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);

  const handleSharePress = useCallback(() => {
    setIsShareSheetOpen(true);
  }, []);

  const handleShareSheetClose = useCallback(() => {
    setIsShareSheetOpen(false);
  }, []);

  const handleDeleteNote = useCallback(
    () =>
      deleteNote({
        variables: { input: { id: note.id } },
        update: (cache, { data, errors }) =>
          handleCreateOrDeleteNote(
            "delete",
            cache,
            data,
            errors,
            note.playerId
          ),
        optimisticResponse: {
          __typename: "Mutation",
          deleteNote: {
            __typename: "Note",
            ...note,
          },
        },
        onCompleted: entry => {
          recordUserGoal({
            value: entry?.deleteNote?.playerId,
            goalType: GoalType.DeleteNote,
          });
          toast.show({
            action: "success",
            description: "Note removed from list",
          });
          AnalyticsTracker.note.logNoteDeleted({
            note_id: entry?.deleteNote?.id,
            player_id: entry?.deleteNote?.playerId,
            title: entry?.deleteNote.title,
            body: entry?.deleteNote.body,
            is_public: entry?.deleteNote.isPublic,
            created_at: entry?.deleteNote.createdAt,
            updated_at: entry?.deleteNote.updatedAt,
          });
        },
      }),
    [deleteNote, note, recordUserGoal, toast]
  );

  const canShareNotes = !(
    currentUser?.organization?.defaultNoteVisibility ?? false
  );

  return (
    <>
      <NotesItemView
        note={note}
        loading={loading}
        onDelete={isOwner ? handleDeleteNote : undefined}
        onShare={isOwner && canShareNotes ? handleSharePress : undefined}
        isOwner={isOwner}
        isSmallScreen={isSmallScreen}
        isDark={isDark}
      />
      {isOwner && canShareNotes && (
        <NoteShareSheet
          noteId={note.id}
          isOpen={isShareSheetOpen}
          onClose={handleShareSheetClose}
        />
      )}
    </>
  );
};

export default NotesItemContainer;
